/**
 * Voxyframe Options Page React App
 *
 * This file renders the options page for the Voxyframe framework in WordPress.
 * It handles loading options, rendering dynamic fields, conditional logic,
 * saving options, and displaying notices.
 */

import { render, useState, useEffect } from '@wordpress/element';
import { Button, Spinner, SlotFillProvider } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

import { checkConditionalLogic } from '../../utils/ConditionalLogicChecker';
import { fieldComponents, defaultValues } from '../../components/fieldComponents';
import { fetchOption, saveOptions } from '../../utils/api';
import Header from '../../components/layout/Header';
import Body from '../../components/layout/Body';
import Tabs from '../../components/layout/Tabs';
import { useNotice, NoticeProvider } from '../../contexts/NoticeContext';

const App = () => {
	// Initialize page and fields from global window object
	let page = null;
	let fields = [];

	try {
		const data = window?.VoxyframeOptionsPageData;

		if (
			typeof data === 'object' &&
			data !== null &&
			typeof data.page === 'object' &&
			Array.isArray(data.fields)
		) {
			page = data.page;
			fields = data.fields;
		} else {
			console.warn('[Voxyframe] VoxyframeOptionsPageData is invalid or incomplete.');
		}
	} catch (err) {
		console.error('[Voxyframe] Failed to access VoxyframeOptionsPageData:', err);
	}

	// React state
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const { showNotice } = useNotice();

	/**
	 * Load initial form data
	 */
	useEffect(() => {
		const loadData = async () => {
			if (!page?.option_key) {
				// No saved options, just use defaults
				const initial = {};
				fields.forEach((field) => {
					initial[field.field_key] = field.default_value ?? defaultValues[field.type] ?? '';
				});
				setFormData(initial);
				setLoading(false);
				return;
			}

			try {
				const fetched = await fetchOption(page.option_key);
				const initial = {};

				fields.forEach((field) => {
					const fieldKey = field.field_key;
					const tabName = field.tab || 'settings';

					// Get saved value from API
					const savedValue =
						page?.tabs?.length > 0
							? fetched?.[tabName]?.[fieldKey]
							: fetched?.[fieldKey];

					// Simple clean logic:
					// savedValue > field.default_value > global default
					let finalValue =
						savedValue !== undefined && savedValue !== null && savedValue !== ''
							? savedValue
							: field.default_value ?? defaultValues[field.type] ?? '';

					initial[fieldKey] = finalValue;
				});

				setFormData(initial);
			} catch (error) {
				console.error('[Voxyframe] Failed to load saved options:', error);
				showNotice(__('Failed to load saved options.', 'voxycure-framework'), 'error');
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, []);

	/**
	 * Save form data
	 */
	const handleSave = async () => {
		setIsSaving(true);
		setFieldErrors({});

		let valuesToSave;
		if (page?.tabs?.length > 0) {
			const tabGroupedValues = {};
			fields.forEach((field) => {
				const tabName = field.tab || 'settings';
				if (!tabGroupedValues[tabName]) tabGroupedValues[tabName] = {};
				tabGroupedValues[tabName][field.field_key] = formData[field.field_key];
			});
			valuesToSave = tabGroupedValues;
		} else {
			valuesToSave = { ...formData };
		}

		try {
			const { success, status, body } = await saveOptions({
				id: page?.id,
				option_key: page?.option_key,
				values: valuesToSave,
			});

			if (success) {
				showNotice(
					body?.message || __('Options saved successfully.', 'voxycure-framework'),
					'success'
				);
				return;
			}

			if (status === 422 && body?.error_fields) {
				const errorMap = {};
				body.error_fields.forEach(({ field }) => {
					errorMap[field] = true;
				});
				setFieldErrors(errorMap);
				showNotice(
					__('Please fix the highlighted fields and try again.', 'voxycure-framework'),
					'error'
				);
			} else {
				showNotice(__('Failed to save options. Please try again.', 'voxycure-framework'), 'error');
			}
		} catch (error) {
			console.error('[Voxyframe] Failed to save options:', error);
			showNotice(__('Failed to save options. Please try again.', 'voxycure-framework'), 'error');
		} finally {
			setIsSaving(false);
		}
	};

	/**
	 * Render fields for a specific tab or all fields
	 */
	const renderFields = (tabName = null) => {
		const filteredFields = fields.filter((field) => {
			const matchesTab = tabName ? field.tab === tabName : true;
			return matchesTab && (checkConditionalLogic ? checkConditionalLogic(formData, field.conditional_logic) : true);
		});

		if (filteredFields.length === 0) {
			return (
				<div className="components-notice is-warning">
					{__('No fields in this section.', 'voxycure-framework')}
				</div>
			);
		}

		return filteredFields.map((field) => {
			const FieldComponent = fieldComponents[field.type] || fieldComponents['text'];
			const fieldKey = field.field_key;
			const value = formData[fieldKey] ?? defaultValues[field.type] ?? '';

			const onChange = (val) => {
				const parsedValue = field.type === 'number' ? Number(val) : val;
				setFormData((prev) => ({
					...prev,
					[fieldKey]: parsedValue,
				}));
			};

			return (
				<FieldComponent
					key={fieldKey || field.label}
					field={field}
					value={value}
					onChange={onChange}
					className={fieldErrors[fieldKey] ? 'voxycure-error-field' : ''}
				/>
			);
		});
	};

	/**
	 * Prepare tab list for UI
	 */
	const tabList =
		page?.tabs?.length > 0
			? page.tabs
				.filter((t) => t.value?.trim() !== '' && t.label?.trim() !== '')
				.map((t) => ({
					name: t.value,
					title: t.label || t.value,
					className: `tab-${t.name || t.value}`,
				}))
			: [];

	// Render UI
	return (
		<div className="voxycure-editor__content options-page">
			<Header>
				<h2 className="voxycure-editor__header-title">
					{page?.page_title || __('Options Page', 'voxycure-framework')}
				</h2>
				<Button
					className="components-button is-primary voxycure-button"
					onClick={handleSave}
					disabled={isSaving}
				>
					{isSaving ? (
						<>
							<Spinner style={{ verticalAlign: 'middle', marginRight: 6 }} />
							{__('Saving...', 'voxycure-framework')}
						</>
					) : (
						__('Save', 'voxycure-framework')
					)}
				</Button>
			</Header>

			<Body>
				{loading ? (
					<Spinner />
				) : tabList.length > 0 ? (
					<Tabs
						tabs={tabList}
						alignment={page?.tab_alignment || 'horizontal'}
						renderTab={(tab) => renderFields(tab.name)}
					/>
				) : fields.length === 0 ? (
					<div className="components-notice is-warning">
						{__('No fields available for this options page.', 'voxycure-framework')}
					</div>
				) : (
					<div className="voxycure-card">
						<div className="components-tab-panel__tab-content">{renderFields()}</div>
					</div>
				)}
			</Body>
		</div>
	);
};

// Mount React App
const rootElement = document.getElementById('voxycure-options-page-root');

if (rootElement) {
	render(
		<SlotFillProvider>
			<NoticeProvider>
				<App />
			</NoticeProvider>
		</SlotFillProvider>,
		rootElement
	);
} else {
	console.error('[Voxyframe] Could not find #voxycure-options-page-root to mount the app.');
}
